/**
 * Estado real de las transferencias de una confirmación (BB-1643).
 *
 * Responde la pregunta que el audit log NO responde: el registro dice qué se confirmó, con qué filtro
 * y cuándo; esto dice si el dinero se movió. Lo calcula `dailyReconciliation` (BB-1642) cruzando el
 * ledger de viajes enviados con `int_mtm_payments_transfers`, y lo deja en un documento de Firestore
 * por confirmación que el front lee directo con el SDK de cliente.
 *
 * Aquí solo vive la derivación pura: mapear el documento, ordenar los buckets y detectar descuadres.
 * Ni Firestore ni React — igual que `auditLog.ts` está separado de `auditLogFirestore.ts`, para poder
 * testear esto en node y reusarlo desde el harness.
 *
 * DOS COSAS QUE NO SE HACEN AQUÍ, y son deliberadas:
 *  - **No se suma dinero.** Los importes llegan como string desde BigQuery y se formatean; el front no
 *    agrega euros (contrato: "el front no agrega nada que Ops lea como esto es lo que se va a pagar").
 *  - **No se filtra ningún estado desconocido.** El vocabulario lo produce el pipeline y puede crecer:
 *    un estado nuevo se pinta con su valor crudo antes que desaparecer de la pantalla.
 */

import { DEFINICIONES_MOTIVO_FALLO, type MotivoFalloTransferencia } from '@caes-payments/shared';

/**
 * Por qué Stripe rechazó parte de un bucket (BB-2622). Solo en FAILED / CANCELED / UNPROCESSABLE.
 * `motivo` es string y no el enum: si el backend añade un motivo antes que el front, se pinta crudo.
 */
export interface MotivoBucket {
  motivo: string;
  providerResultCode: string | null;
  nViajes: number;
  /** Conductores distintos: a cuántas personas hay que contactar. */
  nUsuarios: number;
  importeEur: string;
}

/**
 * En qué punto está la parte de un bucket `BLOCKED_BY_CERTIFICATION` (BB-2624). `subestado` es
 * string por lo mismo: el vocabulario va a crecer (DAT-571, reversiones por blacklist) y un valor que
 * el front aún no conoce tiene que verse, no desaparecer.
 */
export interface SubestadoBucket {
  subestado: string;
  nViajes: number;
  importeEur: string;
}

/** Un bucket del cruce: cuántos viajes acabaron en ese estado y cuánto dinero suman. */
export interface BucketEstado {
  estado: string;
  nViajes: number;
  /** Decimal como string, tal cual lo calculó BigQuery. `null` cuando no hay transferencia. */
  importeEur: string | null;
  /** Ausente en documentos anteriores a BB-2622, o si el desglose no cuadraba con el bucket. */
  motivos?: MotivoBucket[];
  /** Ausente en documentos anteriores a BB-2624, o si el desglose no cuadraba con el bucket. */
  subestados?: SubestadoBucket[];
}

export interface EstadoConfirmacion {
  previewId: string;
  /** Cuándo corrió el cruce. Es una FOTO, no un sello: mañana puede decir otra cosa. */
  calculadoEnIso: string | null;
  /** Frescura del modelo de transferencias en el momento del cruce. */
  transferenciasAlDiaIso: string | null;
  /** Viajes que la confirmación envió de verdad, según el ledger. */
  nViajesEnviados: number;
  buckets: BucketEstado[];
}

/** El bucket que da nombre al problema: enviado, aceptado con 202, y sin transferencia creada. */
export const SIN_TRANSFERENCIA = 'NO_TRANSFER';

/**
 * Cómo se pinta cada cosa (BB-2625). Tres tonos y no dos, porque el 2026-10-01 la pantalla gastaba
 * el rojo en cosas que no lo eran:
 *  - `alarma`: alguien de nuestro lado o de Backend tiene que mirar. Es lo único en rojo y lo único
 *    que cuenta el chip "a revisar".
 *  - `aviso`: Stripe rechazó la transferencia por la cuenta del conductor. No es un fallo de la
 *    consola ni se arregla volviendo a pagar: lo resuelve el conductor (o nadie). Ámbar.
 *  - `neutro`: lo normal, lo que se resuelve solo, o lo que no se puede decidir.
 */
export type Tono = 'alarma' | 'aviso' | 'neutro';

/**
 * Etiquetas en castellano. Diccionario ABIERTO: lo que no esté aquí se pinta con su valor crudo, que
 * es feo pero honesto — mejor un `WEIRD_NEW_STATE` en pantalla que un bucket que no existe.
 *
 * `PENDING` dice "certificado": la consola termina ahí, y el pago final lo lanza Backend a mano. Sin
 * esa palabra se leía como algo que faltaba por nuestra parte.
 */
const ETIQUETAS: Record<string, string> = {
  PAID: 'Pagado',
  PENDING: 'Certificado, pendiente de pago',
  CANCELED: 'Cancelado',
  FAILED: 'Fallido',
  UNPROCESSABLE: 'No procesable',
  BLOCKED_BY_CERTIFICATION: 'Bloqueado por certificación',
  [SIN_TRANSFERENCIA]: 'Aceptado sin transferencia creada',
};

/**
 * La etiqueta del bucket. `FAILED` con motivos pasa a "Rechazado por Stripe": los motivos prueban que
 * la transferencia existe y que la rechazó Stripe. Sin ellos (documento antiguo) no se sabe, y se
 * queda en "Fallido" en vez de afirmar una causa.
 */
export function etiquetaEstado(estado: string, bucket?: Pick<BucketEstado, 'motivos'>): string {
  if (estado === 'FAILED' && bucket?.motivos?.length) return 'Rechazado por Stripe';
  return ETIQUETAS[estado] ?? estado;
}

const SUBESTADOS: Record<string, { etiqueta: string; definicion: string; tono: Tono }> = {
  no_enviado: {
    etiqueta: 'Aún no enviado',
    definicion:
      'Su tanda todavía no se ha pagado, o la petición falló y Backend no la aceptó. No ha llegado a Backend.',
    tono: 'neutro',
  },
  enviado_tras_el_corte: {
    etiqueta: 'Enviado, sin reflejar todavía',
    definicion:
      'Se envió después de la foto de transferencias que usa el cruce (o en las 2 h anteriores). Lo normal: mañana debería verse movido.',
    tono: 'neutro',
  },
  aceptado_sin_mover: {
    etiqueta: 'Aceptado y sin mover',
    definicion:
      'Se envió con margen antes de la foto, Backend lo aceptó y la transferencia sigue bloqueada. Hay que llevarlo a Backend.',
    tono: 'alarma',
  },
  sin_hora_de_envio: {
    etiqueta: 'Sin hora de envío',
    definicion:
      'Confirmación anterior a que se guardara la hora real de envío: no se puede saber si está pendiente o atascado. Si importa, hay que cruzarlo a mano.',
    tono: 'neutro',
  },
  // DAT-571 (#134/#135). Reversión temporal de Backend (2026-09-30/10-01), no un fallo: en gris.
  revertido_por_blacklist: {
    etiqueta: 'Revertido por blacklist',
    definicion:
      'Backend aceptó el viaje y revirtió la certificación porque el conductor o el pasajero estaba en blacklist. No es un fallo: Backend lo hizo a propósito el 30 de septiembre y el 1 de octubre de 2026, mientras la consola no tenía filtro de blacklist. El viaje vuelve a ser pagable: si se reenvía, ahora se paga.',
    tono: 'neutro',
  },
  // DAT-571. Un "aceptado y sin mover" con el origen identificado: sigue siendo alarma (lo pide el
  // contrato expresamente), no se pinta en gris por tener explicación.
  retenido_backfill: {
    etiqueta: 'Retenido por el backfill de julio',
    definicion:
      'Backend lo aceptó, pero la transferencia sigue retenida por el backfill de corte de CAEs de julio (TRI-48) y no se ha tocado desde entonces. Hay que llevarlo a Backend.',
    tono: 'alarma',
  },
};

/**
 * Las etiquetas de los subestados viven aquí y no en `shared/` porque el contrato no las define
 * todavía (sí lo hace con los motivos, `DEFINICIONES_MOTIVO_FALLO`). Si llegan al contrato, esto
 * pasa a leerlas de allí. Un subestado desconocido se pinta crudo y en neutro: no se gasta el rojo
 * en algo que la pantalla no sabe interpretar. Por eso cada valor nuevo del contrato tiene que
 * entrar aquí con su tono: `retenido_backfill` habría salido en gris siendo una alarma.
 */
export function describirSubestado(subestado: string): {
  etiqueta: string;
  definicion: string | null;
  tono: Tono;
} {
  return SUBESTADOS[subestado] ?? { etiqueta: subestado, definicion: null, tono: 'neutro' };
}

/**
 * Motivo de fallo, con la etiqueta y la definición del contrato (una sola fuente: front y back dicen
 * lo mismo). `otro` es una alarma: es un rechazo que nadie ha clasificado y hay que revisar con
 * Backend. Los transitorios se resuelven solos, así que van en neutro.
 */
export function describirMotivo(m: Pick<MotivoBucket, 'motivo' | 'providerResultCode'>): {
  etiqueta: string;
  definicion: string | null;
  tono: Tono;
  transitorio: boolean;
} {
  const def = (DEFINICIONES_MOTIVO_FALLO as Partial<typeof DEFINICIONES_MOTIVO_FALLO>)[
    m.motivo as MotivoFalloTransferencia
  ];
  if (!def) return { etiqueta: m.motivo, definicion: null, tono: 'alarma', transitorio: false };
  const etiqueta =
    m.motivo === 'otro' && m.providerResultCode ? `${def.etiqueta} (${m.providerResultCode})` : def.etiqueta;
  const tono: Tono = def.transitorio ? 'neutro' : m.motivo === 'otro' ? 'alarma' : 'aviso';
  return { etiqueta, definicion: def.definicion, tono, transitorio: def.transitorio };
}

/**
 * Estados que piden que alguien mire cuando NO hay desglose que diga más. `FAILED` está porque sin
 * motivos no se sabe si fue Stripe o otra cosa; `NO_TRANSFER` es el fallo silencioso que todo esto
 * existe para encontrar.
 */
const ESTADOS_EN_ALARMA_SIN_DESGLOSE = new Set([SIN_TRANSFERENCIA, 'FAILED', 'UNPROCESSABLE']);

/** Viajes de un bucket en cada tono. Con desglose manda el desglose; sin él, el estado. */
export function viajesPorTono(b: BucketEstado): Record<Tono, number> {
  const cuenta: Record<Tono, number> = { alarma: 0, aviso: 0, neutro: 0 };
  if (b.subestados?.length) {
    for (const s of b.subestados) cuenta[describirSubestado(s.subestado).tono] += s.nViajes;
  } else if (b.motivos?.length) {
    for (const m of b.motivos) cuenta[describirMotivo(m).tono] += m.nViajes;
  } else {
    cuenta[ESTADOS_EN_ALARMA_SIN_DESGLOSE.has(b.estado) ? 'alarma' : 'neutro'] += b.nViajes;
  }
  return cuenta;
}

/** Si el bucket tiene algo en rojo. */
export function pideAtencion(b: BucketEstado): boolean {
  return viajesPorTono(b).alarma > 0;
}

/**
 * Mapea el documento de Firestore.
 *
 * Defensivo a propósito: este documento lo escribe otro proceso, así que un campo que falta o llega
 * con otro tipo no puede tumbar la pantalla del registro. Lo que no se entiende se descarta como
 * bucket y se deja constancia en el descuadre, que es visible.
 *
 * `calculatedAt` es un Timestamp de Firestore, no un string: llega como objeto con `toDate()`. Se
 * acepta también un ISO plano para que el harness y las fixtures puedan escribir el caso sin
 * construir un Timestamp.
 */
export function estadoDeDocumento(previewId: string, data: Record<string, unknown>): EstadoConfirmacion {
  const brutos = Array.isArray(data.byState) ? data.byState : [];

  const buckets: BucketEstado[] = [];
  for (const bruto of brutos) {
    if (typeof bruto !== 'object' || bruto === null) continue;
    const fila = bruto as Record<string, unknown>;
    if (typeof fila.state !== 'string' || fila.state === '') continue;
    const nViajes = typeof fila.nTrips === 'number' ? fila.nTrips : 0;
    const bucket: BucketEstado = {
      estado: fila.state,
      nViajes,
      importeEur: typeof fila.amountEur === 'string' ? fila.amountEur : null,
    };
    const motivos = leerMotivos(fila.motivos, nViajes);
    if (motivos) bucket.motivos = motivos;
    const subestados = leerSubestados(fila.subestados, nViajes);
    if (subestados) bucket.subestados = subestados;
    buckets.push(bucket);
  }

  return {
    previewId,
    calculadoEnIso: aIso(data.calculatedAt),
    transferenciasAlDiaIso: typeof data.transfersAsOf === 'string' ? data.transfersAsOf : null,
    nViajesEnviados: typeof data.nTripsSent === 'number' ? data.nTripsSent : 0,
    buckets: ordenarBuckets(buckets),
  };
}

/**
 * Orden de lectura: primero lo que pide atención —aunque sean 3 viajes de 20.000—, y dentro de cada
 * grupo por dinero descendente.
 *
 * Ordenar solo por importe escondería un `NO_TRANSFER` de 4 viajes al final de la lista, que es
 * justo el bucket por el que existe esta pantalla. El importe se compara con `Number` SOLO para
 * ordenar: no se suma ni se muestra el resultado.
 */
export function ordenarBuckets(buckets: readonly BucketEstado[]): BucketEstado[] {
  return [...buckets].sort((a, b) => {
    const atencion = Number(pideAtencion(b)) - Number(pideAtencion(a));
    if (atencion !== 0) return atencion;
    const importe = Number(b.importeEur ?? '0') - Number(a.importeEur ?? '0');
    if (importe !== 0) return importe;
    return b.nViajes - a.nViajes;
  });
}

/** Viajes repartidos entre buckets. Solo para comprobar el cuadre, no para pintar un total. */
export function viajesEnBuckets(estado: EstadoConfirmacion): number {
  return estado.buckets.reduce((n, b) => n + b.nViajes, 0);
}

/**
 * `true` cuando los viajes del cruce no cuadran con los que dice el ledger.
 *
 * Es la red de la misma clase que `descuadra()` en ResumenPanel: si el cruce pierde o duplica viajes,
 * se ve en pantalla en vez de quedar como un total plausible. Con el ledger vacío (0 enviados y 0
 * buckets) NO hay descuadre — eso es el caso "todavía no hay nada", no un error.
 */
export function descuadraElCruce(estado: EstadoConfirmacion): boolean {
  return viajesEnBuckets(estado) !== estado.nViajesEnviados;
}

/** Cuántos viajes están en alarma (rojo). 0 = nada que revisar. */
export function nViajesQuePidenAtencion(estado: EstadoConfirmacion): number {
  return estado.buckets.reduce((n, b) => n + viajesPorTono(b).alarma, 0);
}

/**
 * Cuántos viajes rechazó Stripe por la cuenta del conductor (ámbar). Va aparte del chip rojo: siguen
 * sin pagarse y Soporte tiene que verlos, pero no son un fallo que revisar en la consola.
 */
export function nViajesRechazadosPorStripe(estado: EstadoConfirmacion): number {
  return estado.buckets.reduce((n, b) => n + viajesPorTono(b).aviso, 0);
}

/**
 * Desgloses de un bucket. Se descartan ENTEROS (y el bucket se pinta como antes, por su estado) si
 * alguna fila es ilegible o si no suman exactamente los viajes del bucket: un desglose parcial
 * pintaría unos motivos como si fueran todos, que es peor que no tenerlo. El backend aplica la misma
 * regla al escribirlos (`handleReconciliation`); esto es la segunda red, por si cambia.
 */
function leerMotivos(bruto: unknown, nViajesBucket: number): MotivoBucket[] | undefined {
  if (!Array.isArray(bruto) || bruto.length === 0) return undefined;
  const filas: MotivoBucket[] = [];
  for (const x of bruto) {
    if (typeof x !== 'object' || x === null) return undefined;
    const f = x as Record<string, unknown>;
    if (typeof f.motivo !== 'string' || f.motivo === '' || typeof f.nTrips !== 'number') return undefined;
    filas.push({
      motivo: f.motivo,
      providerResultCode: typeof f.providerResultCode === 'string' ? f.providerResultCode : null,
      nViajes: f.nTrips,
      nUsuarios: typeof f.nUsuarios === 'number' ? f.nUsuarios : 0,
      importeEur: typeof f.amountEur === 'string' ? f.amountEur : '0',
    });
  }
  return sumaExacta(filas, nViajesBucket) ? filas : undefined;
}

function leerSubestados(bruto: unknown, nViajesBucket: number): SubestadoBucket[] | undefined {
  if (!Array.isArray(bruto) || bruto.length === 0) return undefined;
  const filas: SubestadoBucket[] = [];
  for (const x of bruto) {
    if (typeof x !== 'object' || x === null) return undefined;
    const f = x as Record<string, unknown>;
    if (typeof f.subestado !== 'string' || f.subestado === '' || typeof f.nTrips !== 'number') {
      return undefined;
    }
    filas.push({
      subestado: f.subestado,
      nViajes: f.nTrips,
      importeEur: typeof f.amountEur === 'string' ? f.amountEur : '0',
    });
  }
  return sumaExacta(filas, nViajesBucket) ? filas : undefined;
}

/** Solo viajes, enteros: es un cuadre, no una suma de dinero. */
function sumaExacta(filas: Array<{ nViajes: number }>, total: number): boolean {
  return filas.reduce((n, f) => n + f.nViajes, 0) === total;
}

/**
 * Timestamp de Firestore, ISO plano, o nada.
 *
 * Se acepta el objeto con `toDate()` porque es lo que devuelve el SDK de cliente, y el string porque
 * es lo que escriben las fixtures. Cualquier otra cosa es `null`, y la UI lo trata como "no se sabe
 * cuándo" — que `frescura.ts` ya degrada a aviso en vez de a silencio.
 */
function aIso(valor: unknown): string | null {
  if (typeof valor === 'string' && valor !== '') return valor;
  if (typeof valor === 'object' && valor !== null && 'toDate' in valor) {
    const fecha = (valor as { toDate: () => Date }).toDate();
    return Number.isNaN(fecha.getTime()) ? null : fecha.toISOString();
  }
  return null;
}
